import { ClearOutlined, TrophyOutlined } from '@ant-design/icons';
import { MODERATOR_ROLES, REFERENCE_TYPES } from '@cult/domain';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { Button, Flex, Input, Skeleton, Space } from 'antd';
import { useState } from 'react';
import {
  BlankState,
  type CreatedRange,
  CreatedRangeFilter,
  ExportCsvButton,
  FilterRow,
  FilterRowSpacer,
  InlineError,
  PageHeader,
  createdRangeParams,
  hasCreatedRange,
  useTablePagination,
} from '../../../../components';
import { AdminPraiseDrawer, AdminPraisesTable } from '../../../../features/admin/praises';
import { UserSelect } from '../../../../features/admin/users';
import { type PraiseFilters, buildPraiseParams, usePraisesPaged } from '../../../../features/praises';
import { ReferenceFilter } from '../../../../features/reference';
import { recordNumberParam } from '../../../../lib/search-params';

type Search = {
  praiseId?: string;
};

export const PraisesPage = () => {
  const { praiseId } = Route.useSearch();
  const navigate = useNavigate();
  const [author, setAuthor] = useState<string>('');
  const [coreValues, setCoreValues] = useState<string[]>([]);
  const [frameworks, setFrameworks] = useState<string[]>([]);
  const [search, setSearch] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>('');
  const [dateRange, setDateRange] = useState<CreatedRange>(null);

  const filters: PraiseFilters = createdRangeParams(dateRange);
  if (author) filters.author = author;
  if (coreValues.length > 0) filters.coreValues = coreValues;
  if (frameworks.length > 0) filters.frameworks = frameworks;
  if (search) filters.q = search;

  const { page, pageSize, buildPagination } = useTablePagination(JSON.stringify(filters));
  const praisesQuery = usePraisesPaged(filters, page, pageSize);
  const all = praisesQuery.data?.praises ?? [];
  const hasActiveFilters = Boolean(
    author || coreValues.length > 0 || frameworks.length > 0 || search || hasCreatedRange(dateRange),
  );

  const applySearch = (v: string) => {
    setSearchInput(v);
    setSearch(v.trim());
  };

  const clearFilters = () => {
    setAuthor('');
    setCoreValues([]);
    setFrameworks([]);
    setSearch('');
    setSearchInput('');
    setDateRange(null);
  };

  const openPraise = (id: string) => {
    navigate({ to: '/admin/praises', search: (prev: Search) => ({ ...prev, praiseId: id }) });
  };

  const closePraise = () => {
    navigate({ to: '/admin/praises', search: (prev: Search) => ({ ...prev, praiseId: undefined }) });
  };

  return (
    <Flex vertical gap="large">
      <PageHeader
        title="Praises"
        subtitle="View and edit existing praises"
        action={
          <Space>
            <ExportCsvButton path="/praises/export" params={buildPraiseParams(filters)} fileBaseName="praises" />
            <Link to="/admin/praises/new">
              <Button type="primary" icon={<TrophyOutlined />}>
                Give praise
              </Button>
            </Link>
          </Space>
        }
      />

      <FilterRow>
        <Input.Search
          placeholder="Search praises"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onSearch={applySearch}
          allowClear
          style={{ width: 240 }}
        />
        <UserSelect
          value={author}
          onChange={(v) => setAuthor(v ?? '')}
          allowClear
          roles={[...MODERATOR_ROLES]}
          placeholder="Filter by author"
          style={{ width: 220 }}
        />
        <ReferenceFilter
          type={REFERENCE_TYPES.CORE_VALUE}
          value={coreValues}
          onChange={setCoreValues}
          placeholder="Core values"
        />
        <ReferenceFilter
          type={REFERENCE_TYPES.FRAMEWORK}
          value={frameworks}
          onChange={setFrameworks}
          placeholder="Frameworks"
        />
        <CreatedRangeFilter value={dateRange} onChange={setDateRange} />
        <FilterRowSpacer />
        {hasActiveFilters ? (
          <Button type="text" size="small" icon={<ClearOutlined />} onClick={clearFilters}>
            Reset filters
          </Button>
        ) : null}
      </FilterRow>

      {praisesQuery.isError ? <InlineError title="Couldn't load praises" detail={praisesQuery.error.message} /> : null}

      {praisesQuery.isPending ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : all.length === 0 && !praisesQuery.isError ? (
        <BlankState
          title={hasActiveFilters ? 'No praises match these filters' : 'No praises yet'}
          hint={
            hasActiveFilters
              ? 'Try a different author, core value, framework, date range, or search term — or clear the filters.'
              : 'Praises given from the feed will appear here.'
          }
          action={
            hasActiveFilters ? (
              <Button size="small" onClick={clearFilters}>
                Clear filters
              </Button>
            ) : null
          }
        />
      ) : all.length === 0 ? null : (
        <AdminPraisesTable
          praises={all}
          loading={praisesQuery.isFetching}
          onOpenPraise={openPraise}
          pagination={buildPagination(praisesQuery.data?.total ?? 0)}
        />
      )}

      <AdminPraiseDrawer praiseId={praiseId ?? null} onClose={closePraise} praises={all} onNavigate={openPraise} />
    </Flex>
  );
};

export const Route = createFileRoute('/_auth/admin/praises/')({
  validateSearch: (search: Record<string, unknown>): Search => ({
    praiseId: recordNumberParam(search.praiseId),
  }),
  component: PraisesPage,
});
